// Histogram.jsx
// Histogram Component for displaying and manipulating histograms

// Copyright HS Analysis GmbH, 2019
// Author: Viktor Eberhardt

// Framework imports
import React, { Component } from "react";
import PropTypes from "prop-types";

// Material UI imports
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import GetApp from "@mui/icons-material/GetApp";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Publish from "@mui/icons-material/Publish";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import withStyles from "@mui/styles/withStyles";

// External imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsAltH,
  faChartArea,
  faChartLine,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";

// HSA-KIT imports
import Backend from "../../common/utils/Backend";
import SketchColorPicker from "../../common/components/SketchColorPicker";
import {
  isHexColor,
  isInt,
  roundToDecimal,
  downloadJsonObjectAsFile,
} from "../../common/utils/Utils";

// Constants
const styles = (theme) => ({
  root: {
    overflow: "visible",
  },
  chartContainer: {
    height: 120,
    position: "relative",
    marginBottom: 0,
  },
  rangeSlider: {
    bottom: 12,
  },
  actionButton: {
    display: "inline-block",
    border: "none",
    background: "rgba(0, 0, 0, 0.2)",
    marginRight: "2px",
    height: "35px",
  },
  toggleContainer: {
    boxShadow: "none",
    display: "inline-block",
    verticalAlign: "top",
  },
  title: {
    padding: 5,
    fontSize: 14,
  },
  toolbar: {
    width: "100%",
  },
  toolButton: {
    fontSize: 18,
  },
  toolButtonRight: {
    fontSize: 18,
    float: "right",
  },
  grow: {
    flexGrow: 1,
  },
  gradientBar: {
    height: 5,
    width: "100%",
    background: "linear-gradient(90deg, black, white)",
  },
  dragIndicator: {
    color: "#757575",
    position: "absolute",
    top: 5,
    right: 5,
    cursor: "grab",
  },
  grabbing: {
    cursor: "grabbing",
  },
  color: {
    borderRadius: "2px",
  },
  channelLabel: {
    minWidth: 0,
    padding: "0 2px",
    lineHeight: "18px",
    textTransform: "none",
  },
  popover: {
    position: "absolute",
    zIndex: "2",
  },
  cover: {
    position: "fixed",
    top: "0px",
    right: "0px",
    bottom: "0px",
    left: "0px",
  },
  toggleBtnContentActive: {
    border: "3px solid " + theme.palette.primary.main,
    boxShadow: "0 0 0 2px rgba(63,81,181,0.15)",
    transition:
      "background-color 120ms ease, box-shadow 120ms ease, transform 60ms ease",
  },
  toggleBtnContent: {
    border: "3px solid rgba(0,0,0,0.1)",
    transition: "background-color 120ms ease",
  },
  toggleButtonTight: {
    padding: 0,
    minWidth: 0,
    lineHeight: 1,
    border: "0 !important",
    borderRadius: 0,
    margin: "0 1px 1px 0",
  },
  channelItem: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0,
    padding: 0,
    margin: 0,
  },
  channelSwatchWrapper: {
    margin: 0,
    padding: 0,
    lineHeight: 0,
  },
  channelLabelActive: {
    fontWeight: 600,
  },
  channelLabelInactive: {
    color: "#777",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "8px 0",
  },
});

// Histogram Scale Enum
const HistogramScale = Object.freeze({
  LINEAR: "linear",
  LOGARITHMIC: "logarithmic",
});

// Fit mode enum
const HistogramFit = Object.freeze({
  NONE: "none",
  BEST_FIT: "bestFit",
  MIN_MAX: "minMax",
});

/**
 * convert hex colors to rgb values with alpha
 * @param {String} hex Hax Color Code
 * @param {Number} alpha Alpha value
 */
function hexToRGB(hex, alpha) {
  var r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return "rgb(" + r + ", " + g + ", " + b + ")";
  }
}

function validateGamma(gamma) {
  return gamma > 2 ? Math.pow(gamma, 1 / 4) : gamma;
}

/**
 * Configuration of a single Fluorescence Channel.
 * Settings used in histogram and image rendering.
 */
export class ChannelConfig {
  /**
   * Create a new Channel Configuration.
   * @param {string} color The color of the channel. Will be displayed in the histogram and change the color of the channel in the image.
   * @param {boolean} enabled If the channel is diplayed in the histogram and the image.
   * @param {int} min Left border of the histogram. Between 0 and 255.
   * @param {int} max Right border of the histogram. Between 0 and 255.
   * @param {float} gamma The gamma value of the channel. Between 0 and 4.
   * @param {string} name The name of the channel.
   * @param {string} type The type of the channel. Flourscence or Brightfield.
   */
  constructor(color, enabled, min, max, gamma, name, type) {
    if (!isHexColor(color))
      throw TypeError(
        `color must be a valid hexColor, received ${typeof color}: ${color}`,
      );
    if (typeof enabled !== "boolean")
      throw TypeError(
        `enabled must be a boolean, received ${typeof enabled}: ${enabled}`,
      );
    if (!isInt(min) || min < 0 || min > 255)
      throw TypeError(
        `min must be a number between 0 and 255, received ${typeof min}: ${min}`,
      );
    if (!isInt(max) || max < 0 || max > 255)
      throw TypeError(
        `max must be an integer between 0 and 255, received ${typeof max}: ${max}`,
      );
    if (typeof gamma !== "number" || gamma < 0 || gamma > 4)
      throw TypeError(
        `gamma must be a number between 0 and 4, received ${typeof gamma}: ${gamma}`,
      );
    if (typeof name !== "string")
      throw TypeError(
        `name must be a string, received ${typeof name}: ${name}`,
      );
    if (typeof type !== "string")
      throw TypeError(
        `type must be a string, received ${typeof type}: ${type}`,
      );

    this.color = color;
    this.enabled = enabled;
    this.min = min;
    this.max = max;
    this.gamma = gamma;
    this.name = name;
    this.type = type;
    // Manual override values (optional). When set, auto-fit will apply these afterwards.
    this.manualMin = undefined;
    this.manualMax = undefined;
  }

  /**
   * Create a new ChannelConfig from an object without needing to define all parameters individually.
   * Accepts camelCase, PascalCase, and snake_case properties, with camelCase being the default.
   * Additional properties will be ignored,
   * missing properties will use defaults or throw error per ChannelConfig constructor.
   * @param {object} obj An object containing all necessary properties of a ChannelConfig.
   * @returns {ChannelConfig} A new ChannelConfig
   */
  static fromObject(obj) {
    const cfg = new ChannelConfig(
      obj.color ?? obj.Color,
      obj.enabled ?? obj.Enabled,
      obj.min ?? obj.Min,
      obj.max ?? obj.Max,
      obj.gamma ?? obj.Gamma,
      obj.name ?? obj.Name,
      obj.type ?? obj.Type,
    );
    // Load optional manual overrides
    const mMin = obj?.manualMin ?? obj?.ManualMin ?? obj?.manual_min;
    const mMax = obj?.manualMax ?? obj?.ManualMax ?? obj?.manual_max;
    if (typeof mMin === "number" && !isNaN(mMin)) cfg.manualMin = mMin;
    if (typeof mMax === "number" && !isNaN(mMax)) cfg.manualMax = mMax;
    return cfg;
  }
}

/**
 * Configuration of all Fluorescence Channels of a single file.
 */
export class ChannelsConfig {
  /**
   * Create a new Channels Configuration.
   * @param {Array} channelConfigs All Channel Configurations.
   */
  constructor(channelConfigs) {
    if (!Array.isArray(channelConfigs))
      throw TypeError(
        `channelConfigs must be an array, received ${typeof channelConfigs}: ${channelConfigs}`,
      );
    for (let i = 0; i < channelConfigs.length; i++) {
      if (!(channelConfigs[i] instanceof ChannelConfig))
        throw TypeError(
          `channelConfigs[${i}] must be an instance of ChannelConfig, received ${typeof channelConfigs[
            i
          ]}: ${channelConfigs[i]}`,
        );
    }

    this.channels = channelConfigs;
    this.nChannels = channelConfigs.length;
    this.scale = HistogramScale.LOGARITHMIC;
    // Default to no auto-fit; fit can be explicitly set by user/actions
    this.fit = HistogramFit.NONE;
    this.channelSelectMode = "all"; // "all" or "single"
  }

  /**
   * Create a new Channels Configuration from a JSON array.
   * @param {Array} arr A JSON array containing Channel Configurations.
   * @returns {ChannelsConfig} A new Channels Configuration object.
   */
  static fromArray(arr) {
    if (!Array.isArray(arr))
      throw TypeError(`arr must be an array, received ${typeof arr}: ${arr}`);
    return new ChannelsConfig(arr.map((obj) => ChannelConfig.fromObject(obj)));
  }

  /**
   * Create a new Channels Configuration from a JSON object.
   * @param {object} omeChannels Object containing the Channel Configurations.
   * @returns {ChannelsConfig} A new Channels Configuration object.
   */
  static fromObject(obj) {
    const channels = obj.channels ?? obj.Channels;
    const scale = obj.scale ?? obj.Scale;
    const fit = obj.fit ?? obj.Fit ?? HistogramFit.NONE;
    const channelSelectMode =
      obj.channelSelectMode ??
      obj.ChannelSelectMode ??
      obj.singleChannelMode ??
      obj.SingleChannelMode ??
      "all";

    if (!Object.values(HistogramScale).includes(scale)) {
      throw TypeError(
        `scale must be one of ${Object.values(
          HistogramScale,
        )}, received ${typeof scale}: ${scale}`,
      );
    }

    const config = ChannelsConfig.fromArray(channels);
    config.scale = scale;
    // validate and set fit
    const validFits = Object.values(HistogramFit);
    config.fit = validFits.includes(fit) ? fit : HistogramFit.NONE;
    // set channel selection mode if valid
    config.channelSelectMode =
      channelSelectMode === "single" || channelSelectMode === "all"
        ? channelSelectMode
        : "all";
    return config;
  }

  /**
   * Create a new Channels Configuration from an OME XML object.
   * @param {object} omeChannels OME XML object containing the Channel Configurations.
   * @returns {ChannelsConfig} A new Channels Configuration object.
   */
  static fromOMEChannels(omeChannels) {
    const nChannels = omeChannels.length;
    const channels = [];
    for (let i = 0; i < nChannels; i++) {
      let color = omeChannels[i].color;
      if (color !== -1) {
        if (!isNaN(color)) {
          color = parseInt(color, 10);
          // handle negative color integers
          if (color < 0) color = 16777216 - color;
          // conver decimal to hex
          color = color.toString(16);
          // add leading zeros
          while (color.length < 6 || color.length % 2 !== 0) {
            color = "0" + color;
          }
          // add leading #
          color = "#" + color;
        }
      }
      let channelName =
        omeChannels[i].name === "TL Brightfield" ? "RGB" : omeChannels[i].name;

      channelName =
        channelName === ""
          ? i === 0
            ? "R"
            : i === 1
              ? "G"
              : "B"
          : channelName;
      channels[i] = new ChannelConfig(
        color,
        true,
        roundToDecimal(255 * omeChannels[i].low, 0),
        roundToDecimal(255 * Math.min(omeChannels[i].high, 1)),
        validateGamma(omeChannels[i].gamma),
        omeChannels[i].name,
        omeChannels[i].type,
      );
    }

    // Create with default fit = bestFit
    return new ChannelsConfig(channels);
  }

  /**
   * Check if another Channel Configuration is interchangeable with this one.
   * @param {ChannelsConfig} otherConfig The other Channel Configuration to compare to.
   * @returns {boolean} True if the Channel Configurations are interchangeable, False otherwise.
   */
  isCompatibleWith(otherConfig) {
    if (!(otherConfig instanceof ChannelsConfig)) {
      throw TypeError(
        `otherConfig must be an instance of ChannelConfig, received ${typeof otherConfig}: ${otherConfig}`,
      );
    }

    return (
      this.nChannels === otherConfig.nChannels &&
      this.channels.every(
        (channel, index) => channel.name === otherConfig.channels[index].name,
      )
    );
  }
}

class Histogram extends Component {
  constructor(props) {
    super(props);

    let stateGamma = this.props.histogramConfig?.channels[0].gamma;
    if (stateGamma > 2) {
      stateGamma = Math.pow(stateGamma, 1 / 4);
    }
    this._isMounted = false;
    this.state = {
      originalHistogramConfig: JSON.parse(
        JSON.stringify(props.histogramConfig),
      ),
      selectedSingleChannel: -1,
      selectedChannels: this.getEnabledChannels(),
      prevSelectedChannels: null,
      histograms: [],
      datasets: [],
      displayColorPicker: false,
      singleChannelMode: false,
      channelSelectMode: this.props.channelSelectMode,
      sliderMode: false,
      leftGammaValue: this.gammaFunction(0.25, stateGamma),
      rightGammaValue: this.gammaFunction(0.75, stateGamma),
      min: Math.max(0, props.histogramConfig.channels[0].min),
      max: Math.min(255, props.histogramConfig.channels[0].max),
      gamma: stateGamma,
      isBrightfield: this.isBrightfield(),
    };

    // load small images from backend and calculate histogram
    for (let c = 0; c < props.histogramConfig.nChannels; c++) {
      let img = new Image();
      img.src = Backend.renderRegion({
        id: props.id,
        page: c, // TODO calc page for complex images (timeframes and z stack)
        lv: 0,
        x: 0,
        y: 0,
        debug: false,
      });
      // wait until image is loaded succesfully
      img.onload = () => {
        // calculate histogram
        const histograms = this.state.histograms;
        if (this.state.isBrightfield) {
          histograms[0] = this.calcHist(img, 0);
          histograms[1] = this.calcHist(img, 1);
          histograms[2] = this.calcHist(img, 2);
        } else {
          histograms[c] = this.calcHist(img, 0);
        }

        this.setMountedState({ histograms: histograms });
        if (this._isMounted) this.forceUpdate();
      };
    }
  }

  /**
   * Check if the current image is a Brightfield Image against a
   * set of rules based on the properties of the first channel.
   * @returns {Boolean} True if Brightfield, False if Fluorescence
   */
  isBrightfield() {
    let channels = this.props.histogramConfig.channels;
    if (channels.length > 1) return false;
    return (
      channels[0].type === "brightfield" ||
      channels[0].name === "TL Brightfield" ||
      channels[0].name === "RGB"
    );
  }

  setMountedState(stateObject, callback) {
    if (!this._isMounted) return;

    this.setState(stateObject, () => {
      if (callback) callback();
    });
  }

  componentWillUnmount() {
    // cancel any pending debounced fit apply
    if (this._fitApplyTimer) {
      clearTimeout(this._fitApplyTimer);
      this._fitApplyTimer = null;
    }
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    // Track which channels already received auto-fit for current file
    this._fitAppliedChannels = new Set();
    this._pendingFitChannels = new Set();
    const histogramConfig = this.props.histogramConfig;
    this.drawGammaLine();

    let channels = this.initializeChannels(histogramConfig);
    if (this.state.isBrightfield) {
      channels = [-1];
    }

    let stateObject = this.initializeStateObject(channels);

    if (channels.length === 1 && channels[0] !== -1) {
      this.updateStateForSingleChannel(
        stateObject,
        histogramConfig,
        channels[0],
      );
    }

    this.updateHistogramConfig(stateObject);

    this.setMountedState(stateObject, () => {
      stateObject = {
        ...stateObject,
      };
      // Apply selectedChannels derived from loaded config (if any)
      this.updateChannels(stateObject.selectedChannels);
      this.handleMinMaxRange(
        [stateObject.min, stateObject.max],
        stateObject.gamma,
        false,
      );
      // Only reset to defaults if no project-type config was loaded
      if (!this._loadedFromProjectType) {
        this.handleReset();
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Reset per-channel auto-fit tracking when file changes
    if (prevProps.id !== this.props.id) {
      this._fitAppliedChannels = new Set();
      this._pendingFitChannels = new Set();
      if (this._fitApplyTimer) {
        clearTimeout(this._fitApplyTimer);
        this._fitApplyTimer = null;
      }
    }
  }

  /**
   * Initializes the channels based on the provided histogram configuration.
   *
   * @param {Object} histogramConfig - The configuration object for the histogram.
   * @param {Array} histogramConfig.channels - An array of channel objects.
   * @param {boolean} histogramConfig.channels[].enabled - Indicates if the channel is enabled.
   * @returns {Array<number>} An array of indices for the enabled channels.
   */
  initializeChannels(histogramConfig) {
    return histogramConfig.channels
      .map((val, index) => (val.enabled ? index : null))
      .filter((val) => val !== null);
  }

  /**
   * Initializes the state object for the histogram component.
   *
   * @param {Array} channels - An array of channel objects to be selected.
   * @returns {Object} The initialized state object containing selected channels and dimensions.
   */
  initializeStateObject(channels) {
    return {
      selectedChannels: channels,
      dimensions: {
        width: this.container.offsetWidth,
        height: 120,
      },
    };
  }

  /**
   * Updates the state object for a single channel with the provided histogram configuration.
   *
   * @param {Object} stateObject - The state object to be updated.
   * @param {Object} histogramConfig - The histogram configuration containing channel data.
   * @param {number} channelIndex - The index of the channel to update.
   */
  updateStateForSingleChannel(stateObject, histogramConfig, channelIndex) {
    stateObject.min = histogramConfig.channels[channelIndex].min;
    stateObject.max = histogramConfig.channels[channelIndex].max;
    stateObject.gamma = validateGamma(
      histogramConfig.channels[channelIndex].gamma,
    );
  }

  /**
   * Updates the histogram configuration and state.
   *
   * @param {Object} stateObject - The state object to be updated.
   * @param {Object} newHistogramConfig - The new configuration for the histogram.
   * @param {Object} channel0 - The channel object containing min, max, and gamma values.
   */
  updateConfig(stateObject, newHistogramConfig, channel0) {
    const { onChange } = this.props;
    onChange(newHistogramConfig);
    stateObject.min = channel0.min;
    stateObject.max = channel0.max;
    stateObject.gamma = channel0.gamma;

    this.setMountedState(stateObject);
    this.handleMinMaxRange([channel0.min, channel0.max], channel0.gamma, false);
    this.updateGamma(channel0.gamma);

    // Persist histogram config per project type and channel count in localStorage
    try {
      const projectType = this.props.projectType;
      const nChannels =
        newHistogramConfig?.nChannels ?? newHistogramConfig?.channels?.length;
      if (projectType && nChannels) {
        const storeKey = "local_" + projectType;
        const existing = JSON.parse(localStorage.getItem(storeKey)) || {};
        const configKey = `histogramConfig_c${nChannels}`;
        existing[configKey] = JSON.stringify(newHistogramConfig);
        localStorage.setItem(storeKey, JSON.stringify(existing));
      }
    } catch (e) {
      // non-fatal: ignore persistence errors
      console.warn("Histogram: failed to persist config to localStorage:", e);
    }
  }

  onChangeFit = (e, value) => {
    // value can be null (deselect), map to NONE
    const newVal = value || HistogramFit.NONE;
    const newHistogramConfig = this.props.histogramConfig;
    newHistogramConfig.fit = newVal;
    this.props.onChange(newHistogramConfig);
    if (newVal === HistogramFit.BEST_FIT) {
      this.handleBestFit();
    } else if (newVal === HistogramFit.MIN_MAX) {
      this.handleMinMaxFit();
    } else {
      // no auto action
    }
  };

  handleClickMinMaxFitButton() {
    const newHistogramConfig = this.props.histogramConfig;
    // Toggle behavior: if already active, deactivate; otherwise activate and apply
    if (newHistogramConfig.fit === HistogramFit.MIN_MAX) {
      newHistogramConfig.fit = HistogramFit.NONE;
      this.props.onChange(newHistogramConfig);
      return;
    }
    // Activating Min-Max via button: clear manual overrides so fit computes fresh
    try {
      if (Array.isArray(newHistogramConfig.channels)) {
        for (const ch of newHistogramConfig.channels) {
          if (!ch) continue;
          delete ch.manualMin;
          delete ch.manualMax;
        }
      }
    } catch (e) {
      console.warn("Histogram: failed clearing manual overrides (MinMax)", e);
    }
    newHistogramConfig.fit = HistogramFit.MIN_MAX;
    this.props.onChange(newHistogramConfig);
    this.handleMinMaxFit();
  }

  handleClickBestFitButton() {
    const newHistogramConfig = this.props.histogramConfig;
    // Toggle behavior: if already active, deactivate; otherwise activate and apply
    if (newHistogramConfig.fit === HistogramFit.BEST_FIT) {
      newHistogramConfig.fit = HistogramFit.NONE;
      this.props.onChange(newHistogramConfig);
      return;
    }
    // Activating Best-Fit via button: clear manual overrides so fit computes fresh
    try {
      if (Array.isArray(newHistogramConfig.channels)) {
        for (const ch of newHistogramConfig.channels) {
          if (!ch) continue;
          delete ch.manualMin;
          delete ch.manualMax;
        }
      }
    } catch (e) {
      console.warn("Histogram: failed clearing manual overrides (BestFit)", e);
    }
    newHistogramConfig.fit = HistogramFit.BEST_FIT;
    this.props.onChange(newHistogramConfig);
    this.handleBestFit();
  }

  /**
   * Compute min/max for a single channel using CDF threshold and apply only to that channel.
   * @param {number} threshold
   * @param {number} channelIndex
   */
  applyFitForChannel(threshold, channelIndex) {
    console.log("applyFitForChannel", threshold, channelIndex);
    if (
      this.state.isBrightfield ||
      !this.state.histograms ||
      !this.state.histograms[channelIndex]
    )
      return;

    const cfg = this.props.histogramConfig;
    const ch = cfg?.channels?.[channelIndex];
    if (!ch) return;

    const hasManualMin =
      typeof ch.manualMin === "number" && !isNaN(ch.manualMin);
    const hasManualMax =
      typeof ch.manualMax === "number" && !isNaN(ch.manualMax);

    let min, max;
    if (hasManualMin && hasManualMax) {
      // Skip compute if both manual bounds are provided
      min = Math.max(0, Math.min(255, ch.manualMin));
      max = Math.max(min + 1, Math.min(255, ch.manualMax));
    } else {
      const histo = this.state.histograms[channelIndex];
      if (!histo || histo.length === 0) return;

      const sum = histo.reduce((total, val) => total + val, 0);
      if (sum <= 0) return;

      // find left statistical border
      let calcMin = 0;
      let leftSide = 0;
      for (calcMin = 0; calcMin < histo.length; calcMin++) {
        leftSide += histo[calcMin] / sum;
        if (leftSide > threshold) break;
      }

      // find right statistical border
      let calcMax = 255;
      let rightSide = 0;
      for (calcMax = 255; calcMax >= 0; calcMax--) {
        rightSide += histo[calcMax] / sum;
        if (rightSide > threshold) break;
      }

      calcMin = Math.max(0, Math.min(255, calcMin));
      calcMax = Math.max(calcMin + 1, Math.min(255, calcMax));
      min = hasManualMin ? Math.max(0, Math.min(255, ch.manualMin)) : calcMin;
      max = hasManualMax
        ? Math.max(min + 1, Math.min(255, ch.manualMax))
        : calcMax;
    }

    const newHistogramConfig = this.props.histogramConfig;
    if (
      newHistogramConfig &&
      newHistogramConfig.channels &&
      newHistogramConfig.channels[channelIndex]
    ) {
      newHistogramConfig.channels[channelIndex].min = min;
      newHistogramConfig.channels[channelIndex].max = max;
      this.props.onChange(newHistogramConfig);

      // If the UI is focused on this channel, sync slider state
      const selectedIdx =
        this.state.selectedSingleChannel >= 0
          ? this.state.selectedSingleChannel
          : (this.state.selectedChannels[0] ?? 0);
      if (selectedIdx === channelIndex) {
        this.setMountedState({
          min: min,
          max: max,
        });
      }
    }
  }

  /**
   * Updates the histogram configuration based on the provided state object.
   *
   * This function checks if the project context and project data are available.
   * If available, it finds the selected file by its ID and updates the histogram configuration
   * with the channels from the selected file's histogram. If the histogram channels do not have a type,
   * it assigns a default type based on the number of channels.
   *
   * If the project context or project data is not available, it fetches the histogram configuration
   * from the backend and updates the histogram configuration similarly.
   *
   * @param {Object} stateObject - The state object to update.
   */
  updateHistogramConfig(stateObject) {
    const { histogramConfig } = this.props;

    if (!histogramConfig || !histogramConfig.channels) return;

    // Prefer project-type-local stored config over any per-file backend config
    try {
      const projectType = this.props.projectType;
      const nChannels =
        histogramConfig?.nChannels ?? histogramConfig.channels.length;
      if (projectType && nChannels) {
        const storeKey = "local_" + projectType;
        const stored = JSON.parse(localStorage.getItem(storeKey));
        const configKey = `histogramConfig_c${nChannels}`;
        const raw = stored && stored[configKey] ? stored[configKey] : null;
        if (raw) {
          const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
          const newHistogramConfig = {
            ...histogramConfig,
            channels: parsed.channels,
            nChannels: parsed.nChannels ?? nChannels,
            scale: parsed.scale ?? histogramConfig.scale,
          };
          // Apply persisted fit if valid
          try {
            const parsedFit = parsed.fit ?? parsed.Fit;
            const validFits = Object.values(HistogramFit);
            if (validFits.includes(parsedFit)) {
              newHistogramConfig.fit = parsedFit;
            }
          } catch (eFit) {
            // ignore invalid fit
          }
          // propagate persisted select mode if present
          if (
            parsed.channelSelectMode === "single" ||
            parsed.channelSelectMode === "all"
          ) {
            newHistogramConfig.channelSelectMode = parsed.channelSelectMode;
            stateObject.channelSelectMode = parsed.channelSelectMode;
            try {
              this.props.changeChannelSelectMode(parsed.channelSelectMode);
            } catch (e) {
              // ignore if parent doesn't provide the updater
            }
          }
          newHistogramConfig.channels.forEach((channel) => {
            channel.type = channel.type
              ? channel.type
              : newHistogramConfig.channels.length > 1
                ? "fluorescence"
                : "brightfield";
          });
          // Reflect enabled flags in our local selectedChannels state before applying
          stateObject.selectedChannels = newHistogramConfig.channels
            .map((ch, idx) => (ch.enabled ? idx : null))
            .filter((v) => v !== null);
          this.updateConfig(
            stateObject,
            newHistogramConfig,
            newHistogramConfig.channels[0],
          );
          this._loadedFromProjectType = true;
          return;
        }
      }
    } catch (e) {
      console.warn(
        "Histogram: failed to load project-type config from localStorage:",
        e,
      );
    }
    // If nothing found in local storage, keep the provided histogramConfig (OME-based defaults)
  }

  calcHist(img, channel) {
    // create a temporary canvas to convert our image into imagedata array
    let offScrCan = document.createElement("canvas");
    offScrCan.width = img.width;
    offScrCan.height = img.height;
    let ctx1 = offScrCan.getContext("2d");

    // draw image all over the canvas
    ctx1.drawImage(img, 0, 0, img.width, img.height);

    // read out image data array
    let imgData = ctx1.getImageData(0, 0, img.width, img.height).data;

    // step is 4 beacouse our data array is structured like this:
    // [r0,g0,b0,a0, r1,g1,b1,a1, r2,g2,b2,a2, ...]
    //let step = parseInt((img.width * img.height) / imgData.length, 10);
    //let step = 4;
    let step = parseInt(imgData.length / (img.width * img.height), 10);

    // create zeros array
    let histo = Array.from(Array(256), () => 1);
    let histo2 = Array.from(Array(256), () => 1);

    // count occuracys for each brightness value
    for (let i = 0, n = imgData.length; i < n - 1; i += step) {
      histo[imgData[i + channel]] += 1;
    }

    //interpolate histo values
    for (let i = 2; i < histo.length - 2; i++) {
      let sum = 0;
      let count = 0;
      for (let j = i - 2; j < i + 3; j++) {
        if (histo[j] > 1) {
          sum += histo[j];
          count += 1;
        }
      }
      if (count > 0) {
        histo2[i] = sum / count;
      } else {
        histo2[i] = histo[i];
      }
    }

    return histo2;
  }

  handleColorPickerClick(e, index) {
    this.setMountedState({
      selectedSingleChannel: index,
      displayColorPicker: !this.state.displayColorPicker,
    });
    let selectedChannels = this.state.selectedChannels;
    if (!selectedChannels.includes(index)) {
      selectedChannels.push(index);
      this.setMountedState({
        selectedChannels: selectedChannels,
      });
    }
  }

  handleColorPickerClose() {
    this.setMountedState({ displayColorPicker: false });
  }

  handleColorPickerChange(color, channelIndex) {
    if (this.state.selectedSingleChannel >= 0) {
      const newHistogramConfig = this.props.histogramConfig;
      newHistogramConfig.channels[channelIndex].color = color;
      this.props.onChange(newHistogramConfig);
      this.forceUpdateWithTimeout(10);
    }
  }

  handleLogLin() {
    const newHistogramConfig = this.props.histogramConfig;
    // toggle histogram scale
    Object.assign(newHistogramConfig, {
      scale:
        newHistogramConfig.scale === HistogramScale.LINEAR
          ? HistogramScale.LOGARITHMIC
          : HistogramScale.LINEAR,
    });

    this.props.onChange(newHistogramConfig);
    this.forceUpdateWithTimeout(10);
  }

  gammaFunction(x, gamma) {
    gamma = 2 - gamma;
    let y = gamma > 1 ? gamma ** 4 : gamma;
    return x ** y;
  }

  drawHistogramBackground() {
    const data = this.data;
    if (this.backgroundCanvas) {
      const max = 2 ** this.props.bitDepth - 1;
      let visibleStep = 50;
      switch (this.props.bitDepth) {
        case 10:
          visibleStep = 250;
          break;
        case 12:
          visibleStep = 1000;
          break;
        case 14:
          visibleStep = 2500;
          break;
        case 16:
          visibleStep = 15000;
          break;
        default:
      }
      const ctx = this.backgroundCanvas.getContext("2d");
      const w = this.backgroundCanvas.width;
      const h = this.backgroundCanvas.height;
      ctx.clearRect(0, 0, w, h);
      ctx.beginPath();

      // Vertical lines
      let step = (visibleStep * w) / max;
      ctx.font = "12px Arial";
      ctx.fillStyle = "#ddd";
      for (let s = step, i = 1; s < w; s += step, i++) {
        ctx.moveTo(s, 0);
        ctx.lineTo(s, h);
        ctx.fillText(i * visibleStep, s + 2, 12);
      }

      // Horizontal lines
      step = h / 5;
      for (let s = step; s < h; s += step) {
        ctx.moveTo(0, s);
        ctx.lineTo(w, s);
      }
      ctx.strokeStyle = "#ddd";
      ctx.closePath();
      ctx.stroke();

      let maxValues = 0;

      for (let i = 0; i < data.datasets.length; i++) {
        ctx.beginPath();
        for (let j = 0; j < data.datasets[i].data.length; j++) {
          maxValues = Math.max(maxValues, data.datasets[i].data[j]);
        }
      }

      for (let i = 0; i < data.datasets.length; i++) {
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = data.datasets[i].borderColor;
        ctx.fillStyle = data.datasets[i].backgroundColor;
        ctx.moveTo(0, h + 2);
        ctx.globalAlpha = 1.0;
        for (let j = 0; j < data.datasets[i].data.length; j++) {
          let count = data.datasets[i].data[j];
          let transformedCount = (h * count) / maxValues;
          ctx.lineTo(
            (j * w) / data.datasets[i].data.length,
            h - transformedCount,
          );
        }
        ctx.lineTo(w, h + 2);
        ctx.fill("nonzero");
        ctx.closePath();
        ctx.stroke();

        // After drawing the line for this dataset/channel, queue auto-fit for this channel once if enabled
        if (!this.state.isBrightfield) {
          try {
            const dataset = data.datasets[i];
            const chIndex = dataset && dataset.channelIndex;
            const fit =
              this.props.histogramConfig?.fit || HistogramFit.BEST_FIT;
            if (
              typeof chIndex === "number" &&
              chIndex >= 0 &&
              !this._fitAppliedChannels?.has(chIndex) &&
              (fit === HistogramFit.BEST_FIT || fit === HistogramFit.MIN_MAX)
            ) {
              // mark for one-time application and debounce the apply
              this._fitAppliedChannels.add(chIndex);
              this._pendingFitChannels.add(chIndex);
              this._scheduleApplyPendingFits();
            }
          } catch (e) {
            // ignore draw-time auto-fit errors
          }
        }
      }
    }
  }

  _scheduleApplyPendingFits() {
    if (this._fitApplyTimer) return; // already scheduled
    this._fitApplyTimer = setTimeout(() => {
      this._fitApplyTimer = null;
      if (!this._isMounted || this._pendingFitChannels.size === 0) return;
      const fit = this.props.histogramConfig?.fit || HistogramFit.BEST_FIT;
      if (fit === HistogramFit.NONE) {
        this._pendingFitChannels.clear();
        return;
      }

      const threshold = fit === HistogramFit.BEST_FIT ? 0.0005 : 0.0001;
      const newHistogramConfig = this.props.histogramConfig;
      const pending = Array.from(this._pendingFitChannels);
      this._pendingFitChannels.clear();

      // compute and apply for all pending channels in one update
      for (const channelIndex of pending) {
        if (!newHistogramConfig?.channels) continue;
        const ch = newHistogramConfig.channels[channelIndex];
        if (!ch) continue;
        const hasManualMin =
          typeof ch.manualMin === "number" && !isNaN(ch.manualMin);
        const hasManualMax =
          typeof ch.manualMax === "number" && !isNaN(ch.manualMax);

        let min, max;
        if (hasManualMin && hasManualMax) {
          // Both manual: skip compute
          min = Math.max(0, Math.min(255, ch.manualMin));
          max = Math.max(min + 1, Math.min(255, ch.manualMax));
        } else {
          const histo = this.state.histograms?.[channelIndex];
          if (!histo || histo.length === 0) continue;
          const sum = histo.reduce((total, val) => total + val, 0);
          if (sum <= 0) continue;

          let calcMin = 0;
          let leftSide = 0;
          for (calcMin = 0; calcMin < histo.length; calcMin++) {
            leftSide += histo[calcMin] / sum;
            if (leftSide > threshold) break;
          }

          let calcMax = 255;
          let rightSide = 0;
          for (calcMax = 255; calcMax >= 0; calcMax--) {
            rightSide += histo[calcMax] / sum;
            if (rightSide > threshold) break;
          }

          calcMin = Math.max(0, Math.min(255, calcMin));
          calcMax = Math.max(calcMin + 1, Math.min(255, calcMax));
          min = hasManualMin
            ? Math.max(0, Math.min(255, ch.manualMin))
            : calcMin;
          max = hasManualMax
            ? Math.max(min + 1, Math.min(255, ch.manualMax))
            : calcMax;
        }

        if (ch.min !== min || ch.max !== max) {
          ch.min = min;
          ch.max = max;
        }
      }

      // Single onChange to reduce re-renders
      this.props.onChange(newHistogramConfig);

      // Sync UI sliders if selected channel was updated
      const selectedIdx =
        this.state.selectedSingleChannel >= 0
          ? this.state.selectedSingleChannel
          : (this.state.selectedChannels[0] ?? 0);
      const ch = newHistogramConfig.channels?.[selectedIdx];
      if (ch) {
        this.setMountedState({ min: ch.min, max: ch.max });
      }
    }, 0);
  }

  drawGammaLine() {
    if (this.gammaCanvas) {
      const width = this.gammaCanvas.width;
      const height = this.gammaCanvas.height;
      let ctx = this.gammaCanvas.getContext("2d");
      ctx.clearRect(0, 0, width, height);
      ctx.beginPath();
      ctx.moveTo(0, height);
      for (let i = 0; i < this.gammaCanvas.width; i++) {
        let x = i / this.gammaCanvas.width;
        let res = this.gammaFunction(x, this.state.gamma);
        ctx.lineTo(i, height - res * height);
        ctx.moveTo(i, height - res * height);
      }
      ctx.lineTo(this.gammaCanvas.width, 0);
      ctx.lineWidth = 3;
      ctx.strokeStyle = "#FFFFFF";
      ctx.closePath();
      ctx.stroke();
    }
  }

  // slider callback
  handleMinMaxRange(values, gamma, markManual = true) {
    if (!gamma) return;
    const newHistogramConfig = this.props.histogramConfig;
    for (let selectedChannel of this.state.selectedChannels) {
      if (this.state.channelSelectMode === "all") {
        // change for all channels
        for (let channel of newHistogramConfig.channels) {
          channel.min = values[0];
          channel.max = values[1];
          channel.gamma = gamma;
          if (markManual) {
            channel.manualMin = values[0];
            channel.manualMax = values[1];
          }
        }
      } else {
        // change selected channel only
        const ch = newHistogramConfig.channels[selectedChannel];
        ch.min = values[0];
        ch.max = values[1];
        ch.gamma = gamma;
        if (markManual) {
          ch.manualMin = values[0];
          ch.manualMax = values[1];
        }
      }
    }

    this.drawGammaLine();
    this.props.onChange(newHistogramConfig);
    this.forceUpdateWithTimeout(10);
  }

  forceUpdateWithTimeout(delay) {
    setTimeout(() => {
      if (this._isMounted) this.forceUpdate();
    }, delay);
  }

  /**
   * Handles the change in gamma value for the selected channels.
   * Updates the gamma value in the histogram configuration for each selected channel.
   * Calls the onChange prop with the updated histogram configuration.
   * Forces a component update after a specified timeout.
   */
  handleGammaChange() {
    const newHistogramConfig = this.props.histogramConfig;
    for (let selectedChannel of this.state.selectedChannels) {
      if (
        newHistogramConfig.channels &&
        newHistogramConfig.channels.length > 0
      ) {
        const channelIndex = selectedChannel < 0 ? 0 : selectedChannel;
        if (newHistogramConfig.channels[channelIndex]) {
          newHistogramConfig.channels[channelIndex].gamma = this.state.gamma;
        }
      }
    }
    this.props.onChange(newHistogramConfig);
    this.forceUpdateWithTimeout(10);
  }

  handleReset() {
    let isBrightfield = this.state.isBrightfield;
    let selectedChannels = this.state.selectedChannels;
    if (!isBrightfield) {
      let newHistogramConfig = this.props.histogramConfig;

      for (let i = 0; i < newHistogramConfig.channels.length; i++) {
        newHistogramConfig.channels[i].color =
          this.state.originalHistogramConfig.channels[i].color;
      }
      Object.assign(newHistogramConfig, {
        scale: HistogramScale.LOGARITHMIC,
      });
      this.scale = HistogramScale.LOGARITHMIC;
      selectedChannels = Array.from(
        Array(newHistogramConfig.channels.length),
        (x, index) => index,
      );
      selectedChannels =
        selectedChannels.length > 0 && selectedChannels[0] === 0
          ? [-1]
          : selectedChannels;
    } else {
      selectedChannels = [0, 1, 2];
    }

    this.setMountedState({
      singleChannelMode: false,
      channelSelectMode: "all",
      selectedChannels: selectedChannels,
      min: 0,
      max: 2 ** this.props.bitDepth - 1,
      gamma: 1.0,
      leftGammaValue: 0.25,
      rightGammaValue: 0.75,
    });
    // Clear any manual min/max overrides on reset
    try {
      const cfg = this.props.histogramConfig;
      if (cfg && Array.isArray(cfg.channels)) {
        for (const ch of cfg.channels) {
          delete ch.manualMin;
          delete ch.manualMax;
        }
      }
    } catch (e) {
      // non-fatal
    }
    this.handleMinMaxRange([0, 255], 1.0, false);
    if (!isBrightfield) {
      this.updateChannels(selectedChannels);
      this.activateAllChannels();
      // On reset: explicitly activate and apply Best-Fit
      try {
        const cfg = this.props.histogramConfig;
        cfg.fit = HistogramFit.BEST_FIT;
        this.props.onChange(cfg);
        this.handleBestFit();
      } catch (e) {
        // ignore if cfg is immutable
      }
    }
    // Trigger parent-provided zoom fit if available
    try {
      if (typeof this.props.onResetZoomFit === "function") {
        this.props.onResetZoomFit();
      }
      // Also activate the Zoom-Fit toolbar button (visual + standard handler) if present
      const zoomBtn = document.getElementById("toolZoomFitBtn");
      if (zoomBtn && typeof zoomBtn.click === "function") {
        zoomBtn.click();
      }
    } catch (e) {
      // non-fatal
      console.warn("Histogram: onResetZoomFit callback failed", e);
    }
  }

  handleMinMaxFit() {
    // Apply Min-Max-Fit independently to each active channel, ignoring channelSelectMode
    this.applyPerChannelFit(0.0001);
  }

  handleBestFit() {
    console.log("Applying Best-Fit");
    // Apply Best-Fit independently to each active channel, ignoring channelSelectMode
    this.applyPerChannelFit(0.0005);
  }

  /**
   * Compute per-channel min/max using a CDF threshold and apply to each active channel separately.
   * UI sliders will reflect the currently selected channel (or first active channel if none selected).
   * @param {number} threshold - cumulative fraction cutoff for each side (e.g., 0.0001 for Min-Max-Fit, 0.0005 for Best-Fit)
   */
  applyPerChannelFit(threshold) {
    const { histograms } = this.state;
    if (!histograms || histograms.length === 0) return;

    let newHistogramConfig = this.props.histogramConfig;

    // Determine active channel indices
    const activeIndices = newHistogramConfig.channels
      .map((ch, idx) => (ch && ch.enabled ? idx : null))
      .filter((v) => v !== null);

    if (activeIndices.length === 0) return;

    // Compute and apply per-channel ranges
    for (const idx of activeIndices) {
      const ch = newHistogramConfig.channels[idx];
      const hasManualMin =
        typeof ch.manualMin === "number" && !isNaN(ch.manualMin);
      const hasManualMax =
        typeof ch.manualMax === "number" && !isNaN(ch.manualMax);

      let min, max;

      if (hasManualMin && hasManualMax) {
        // Both set manually: skip expensive computation entirely
        min = Math.max(0, Math.min(255, ch.manualMin));
        max = Math.max(min + 1, Math.min(255, ch.manualMax));
      } else {
        const histo = histograms[idx];
        if (!histo || histo.length === 0) continue;

        const sum = histo.reduce((total, val) => total + val, 0);
        if (sum <= 0) continue;

        // find left statistical border
        let calcMin = 0;
        let leftSide = 0;
        for (calcMin = 0; calcMin < histo.length; calcMin++) {
          leftSide += histo[calcMin] / sum;
          if (leftSide > threshold) break;
        }

        // find right statistical border
        let calcMax = 255;
        let rightSide = 0;
        for (calcMax = 255; calcMax >= 0; calcMax--) {
          rightSide += histo[calcMax] / sum;
          if (rightSide > threshold) break;
        }

        // Clamp range and apply overrides if present
        calcMin = Math.max(0, Math.min(255, calcMin));
        calcMax = Math.max(calcMin + 1, Math.min(255, calcMax));
        min = hasManualMin ? Math.max(0, Math.min(255, ch.manualMin)) : calcMin;
        max = hasManualMax
          ? Math.max(min + 1, Math.min(255, ch.manualMax))
          : calcMax;
      }

      ch.min = min;
      ch.max = max;
    }

    // Push changes
    this.props.onChange(newHistogramConfig);

    // Sync the UI to the selected channel (or first active) for slider display
    const selectedIdx =
      this.state.selectedSingleChannel >= 0
        ? this.state.selectedSingleChannel
        : activeIndices[0];
    const uiMin = newHistogramConfig.channels[selectedIdx]
      ? newHistogramConfig.channels[selectedIdx].min
      : 0;
    const uiMax = newHistogramConfig.channels[selectedIdx]
      ? newHistogramConfig.channels[selectedIdx].max
      : 255;

    this.setMountedState({
      min: uiMin,
      max: uiMax,
    });
    this.forceUpdateWithTimeout(10);
  }

  activateAllChannels() {
    let selectedChannels = Array.from(
      Array(this.props.histogramConfig.nChannels),
      (x, index) => index,
    );
    let validGamma = validateGamma(
      this.props.histogramConfig.channels[selectedChannels[0]].gamma,
    );
    this.setMountedState({
      singleChannelMode: false,
      selectedSingleChannel: selectedChannels[0],
      selectedChannels: selectedChannels,
      channelSelectMode: "all",
      min:
        this.props.histogramConfig.channels[selectedChannels[0]].min < 0
          ? 0
          : this.props.histogramConfig.channels[selectedChannels[0]].min,
      max:
        this.props.histogramConfig.channels[selectedChannels[0]].max > 255
          ? 255
          : this.props.histogramConfig.channels[selectedChannels[0]].max,
      gamma: validGamma,
      leftGammaValue: this.gammaFunction(0.25, validGamma),
      rightGammaValue: this.gammaFunction(0.75, validGamma),
    });
    this.forceUpdateWithTimeout(10);
    // persist channel select mode into histogram config before propagating
    try {
      const cfg = this.props.histogramConfig;
      cfg.channelSelectMode = "all";
    } catch (e) {
      // non-fatal if histogramConfig is immutable
    }
    this.props.changeChannelSelectMode("all");
    this.updateChannels(selectedChannels);
  }

  changeChannelSelectMode(e, value) {
    value = value ? "single" : "all";
    let selectedChannels = this.state.selectedChannels;
    if (value !== "all") {
      selectedChannels = [Math.min.apply(Math, this.state.selectedChannels)];
    }
    let validGamma = validateGamma(
      this.props.histogramConfig.channels[selectedChannels[0]].gamma,
    );
    this.setMountedState({
      singleChannelMode: value === "single",
      selectedSingleChannel: selectedChannels[0],
      selectedChannels: selectedChannels,
      channelSelectMode: value,
      min:
        this.props.histogramConfig.channels[selectedChannels[0]].min < 0
          ? 0
          : this.props.histogramConfig.channels[selectedChannels[0]].min,
      max:
        this.props.histogramConfig.channels[selectedChannels[0]].max > 255
          ? 255
          : this.props.histogramConfig.channels[selectedChannels[0]].max,
      gamma: validGamma,
      leftGammaValue: this.gammaFunction(0.25, validGamma),
      rightGammaValue: this.gammaFunction(0.75, validGamma),
    });
    // persist in histogram config so it is exported/imported as part of config
    try {
      const cfg = this.props.histogramConfig;
      cfg.channelSelectMode = value;
      // onChange will be triggered by updateChannels below to propagate this change
    } catch (err) {
      // ignore if cfg cannot be mutated
    }
    this.props.changeChannelSelectMode(value);
    this.updateChannels(selectedChannels);
  }

  changeChannel(value) {
    //prevent deselecting all
    if (value.length === 0) return;

    if (this.state.channelSelectMode === "single") {
      value = value.filter((v) => v !== this.state.selectedChannels[0]);
    }
    // If new selection becomes "all channels", remember the previous non-all selection
    try {
      const total = this.props.histogramConfig.channels.length;
      const wasAll = this.state.selectedChannels.length === total;
      const isAll = value.length === total;
      if (isAll && !wasAll) {
        this.setMountedState({
          prevSelectedChannels: [...this.state.selectedChannels],
        });
      }
    } catch (_) {
      // ignore guard errors
    }
    let validGamma = validateGamma(
      this.props.histogramConfig.channels[value[0]].gamma,
    );
    this.setMountedState({
      selectedSingleChannel: value[0],
      selectedChannels: value,
      min: Math.max(0, this.props.histogramConfig.channels[value[0]].min),
      max: Math.min(255, this.props.histogramConfig.channels[value[0]].max),
      gamma: validGamma,
      leftGammaValue: this.gammaFunction(0.25, validGamma),
      rightGammaValue: this.gammaFunction(0.75, validGamma),
    });

    this.updateChannels(value);
  }

  areAllChannelsSelected() {
    const channels = this.props.histogramConfig?.channels || [];
    if (channels.length === 0) return false;
    return channels.every((c) => !!c.enabled);
  }

  handleAllClick() {
    // If all are selected (blue), restore previously active channels if we have them
    if (this.areAllChannelsSelected()) {
      const prev = this.state.prevSelectedChannels;
      if (prev && prev.length > 0) {
        this.changeChannel(prev);
      }
      return;
    }
    // Not all selected (gray): remember current selection and activate all
    this.setMountedState({
      prevSelectedChannels: [...this.state.selectedChannels],
    });
    this.activateAllChannels();
  }

  // Toggle a channel from the color swatch single-click (when color picker opens on double-click)
  toggleChannelFromSwatch(index) {
    let value = [...this.state.selectedChannels];

    if (this.state.channelSelectMode === "single") {
      // In single mode, always select the clicked channel
      if (value[0] === index) {
        // Prevent deselecting the last one; keep as is
        return;
      }
      value = [index];
    } else {
      // Multi-select mode
      if (value.includes(index)) {
        // Don't allow deselecting the last remaining channel
        if (value.length > 1) {
          value = value.filter((v) => v !== index);
        } else {
          return;
        }
      } else {
        value = [...value, index];
      }
    }

    this.changeChannel(value);
  }

  /**
   * Updates Channel Comfiguration in histogram like colors, etc.
   * @param {Number[]} selectedChannels Selected Channel Indices
   */
  updateChannels(selectedChannels) {
    if (selectedChannels.length > 0 && selectedChannels[0] === -1) {
      selectedChannels = [0];
    }

    // Hide or show channel layer
    const newHistogramConfig = this.props.histogramConfig;
    newHistogramConfig.channels.forEach((channel, idx) => {
      channel.enabled = selectedChannels.includes(idx);
    });

    const selectedChannelIndices = newHistogramConfig.channels
      .map((c, i) => (c.enabled ? i : null))
      .filter((c) => c !== null);

    this.setMountedState({
      selectedChannels: selectedChannelIndices,
    });

    this.props.onChange(newHistogramConfig);
    this.forceUpdateWithTimeout(10);
  }

  getEnabledChannels() {
    const selectedChannels = [];
    for (let i = 0; i < this.props.histogramConfig.length; i++) {
      if (this.props.histogramConfig.channels[i].enabled) {
        selectedChannels.push(i);
      }
    }
    return selectedChannels.length > 0 ? selectedChannels : [-1];
  }

  updateGamma(gamma) {
    this.setMountedState({
      gamma: gamma,
      leftGammaValue: this.gammaFunction(0.25, gamma),
      rightGammaValue: this.gammaFunction(0.75, gamma),
    });
  }

  updateRange(e) {
    if (!this.state.lastX) {
      this.setMountedState({ lastX: e.clientX });
      return;
    }
    if (!this.state.middleBarActive) return;

    let deltaX =
      (255 * (e.clientX - this.state.lastX)) / this.state.dimensions.width;
    let min = this.state.min;
    let max = this.state.max;

    if (deltaX < 0) {
      let new_min = Math.max(0, min + deltaX);
      deltaX = min > 0 ? new_min - min : 0;
    } else {
      let new_max = Math.min(255, max + deltaX);
      deltaX = new_max < 255 ? new_max - max : 255 - max;
    }

    min = Math.max(0, min + deltaX);
    max = Math.min(255, max + deltaX);

    this.setMountedState({
      min: roundToDecimal(min, 0),
      max: roundToDecimal(max, 0),
      lastX: e.clientX,
    });
  }

  renderHistogram() {
    const { histogramConfig } = this.props;
    const { histograms, dimensions } = this.state;
    const data = this.data;

    if (data.datasets.length === 0) {
      data.datasets = this.state.datasets;
    }
    if (data.datasets.length === 0) {
      return null;
    }
    if (histogramConfig.scale === HistogramScale.LOGARITHMIC) {
      data.datasets = data.datasets.map((dataset) => {
        dataset.data = dataset.data.map((p) => 10 * Math.log(p));
        return dataset;
      });
    }

    if (histograms.length === 0) {
      return (
        <div
          height={this.state.dimensions.height}
          width={this.state.dimensions.width}
        ></div>
      );
    }
    return (
      <div
        onDragStart={(e) => {
          e.preventDefault();
        }}
        onMouseMove={(e) => {
          if (e.buttons === 1) {
            this.updateRange(e);
          } else {
            if (
              this.state.leftBarActive ||
              this.state.middleBarActive ||
              this.state.rightBarActive
            ) {
              this.setMountedState({
                middleBarActive: false,
              });
            }
          }
        }}
        onMouseUp={() => {
          this.handleMinMaxRange(
            [this.state.min, this.state.max],
            this.state.gamma,
          );
        }}
      >
        {dimensions && (
          <React.Fragment>
            <canvas
              width={dimensions.width}
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                background: "#aaa",
              }}
              ref={(c) => {
                this.backgroundCanvas = c;
                this.drawHistogramBackground();
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: (dimensions.width / 255) * this.state.min + "px",
                right: (dimensions.width / 255) * (255 - this.state.max) + "px",
                bottom: 0,
                cursor: "all-scroll",
              }}
              onMouseDown={(e) => {
                this.setMountedState({
                  middleBarActive: true,
                  lastX: e.clientX,
                });
              }}
            >
              {/* Line */}
              <canvas
                style={{ width: "100%", height: "100%" }}
                ref={(c) => {
                  this.gammaCanvas = c;
                  this.drawGammaLine();
                }}
              />
              <div className="slider-vertical-outer" style={{ left: "25%" }}>
                <input
                  ref={(c) => (this.leftGamma = c)}
                  value={this.state.leftGammaValue}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onChange={() => {
                    let x = 0.25;
                    let res = this.leftGamma.value;
                    let gamma = Math.log(res) / Math.log(x);
                    if (gamma > 1) {
                      gamma = gamma ** 0.25;
                    }
                    gamma = 2 - gamma;
                    this.updateGamma(gamma);
                  }}
                  onMouseUp={() => {
                    this.handleGammaChange();
                    this.leftGamma.blur();
                  }}
                  type="range"
                  min="0.01"
                  max="0.99"
                  step="0.01"
                  className="slider-vertical"
                />
              </div>
              <div className="slider-vertical-outer" style={{ left: "75%" }}>
                <input
                  ref={(c) => (this.rightGamma = c)}
                  value={this.state.rightGammaValue}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onChange={() => {
                    let x = 0.75;
                    let res = this.rightGamma.value;
                    let gamma = Math.log(res) / Math.log(x);
                    if (gamma > 1) {
                      gamma = gamma ** 0.25;
                    }
                    gamma = 2 - gamma;
                    if (gamma > 0) {
                      this.updateGamma(gamma);
                    }
                  }}
                  onMouseUp={() => {
                    this.handleGammaChange();
                    this.rightGamma.blur();
                  }}
                  type="range"
                  min="0.01"
                  max="0.99"
                  step="0.01"
                  className="slider-vertical"
                />
              </div>

              <div
                className="hover-label"
                style={{
                  position: "absolute",
                  left: "0",
                  top: "0",
                  padding: "0 5px",
                  fontSize: "10px",
                  background: "#555",
                  color: "white",
                }}
              >
                Min:{" "}
                {Math.round(
                  (this.state.min * (2 ** this.props.bitDepth - 1)) / 255,
                )}
              </div>

              <div
                className="hover-label"
                style={{
                  position: "absolute",
                  right: "0",
                  top: "0",
                  padding: "0 5px",
                  fontSize: "10px",
                  background: "#555",
                  color: "white",
                }}
              >
                Max:{" "}
                {Math.round(
                  (this.state.max * (2 ** this.props.bitDepth - 1)) / 255,
                )}
              </div>
            </div>

            <div
              style={{
                position: "absolute",
                left: "0",
                top: "0",
                borderRight: "2px solid black",
                height: "100%",
                width:
                  (this.state.dimensions.width / 255) * this.state.min + "px",
                background: "rgba(0,0,0,0.5)",
              }}
            ></div>

            <div
              style={{
                position: "absolute",
                right: "0",
                top: "0",
                borderLeft: "2px solid black",
                height: "100%",
                width:
                  (this.state.dimensions.width / 255) * (255 - this.state.max) +
                  "px",
                background: "rgba(0,0,0,0.5)",
              }}
            ></div>

            <div
              className="hover-label"
              style={{
                position: "absolute",
                right: "0",
                bottom: "0",
                padding: "0 5px",
                fontSize: "10px",
                background: "#555",
                color: "white",
              }}
            >
              Gamma:{" "}
              {(this.state.gamma < 1
                ? (2 - this.state.gamma) ** 4
                : 2 - this.state.gamma
              ).toFixed(2, 10)}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }

  /**
   * Exports the histogram parameters as a downloadable JSON file.
   * The file name is generated based on the project context if available,
   * otherwise defaults to "histogram.hishsa".
   */
  onExportHistogramParameters = () => {
    const { projectContext, histogramConfig } = this.props;

    // ensure current select mode is part of the exported config
    try {
      histogramConfig.channelSelectMode = this.state.channelSelectMode;
    } catch (e) {
      // ignore if props are immutable; export proceeds with existing object
    }

    let downloadName = "histogram.hishsa";
    if (projectContext) {
      const strData = projectContext.getProjectStringInfos();
      downloadName =
        strData.name +
        "_" +
        strData.date +
        "_" +
        projectContext.user +
        ".hishsa";
    }
    downloadJsonObjectAsFile(histogramConfig, downloadName);
  };

  /**
   * Handles the import of histogram parameters from a file.
   *
   * @param {Event} e - The event triggered by the file input change.
   *
   * This function reads a JSON file containing histogram configuration,
   * validates the configuration, and updates the histogram settings accordingly.
   * If the configuration is not compatible, an error message is displayed.
   *
   * TODO: Possibly use the requestFileInput method? Utils.jsx/requestFileInput
   */
  onImportHistogramParameters = (e) => {
    let files = e.target.files;
    if (files.length <= 0) return false;

    let fr = new FileReader();
    fr.onload = (e) => {
      try {
        let selectedCs = [];
        let loadedConfig = JSON.parse(e.target.result);
        if (
          this.props.histogramConfig.channels.length !==
          loadedConfig.channels.length
        ) {
          window.showErrorSnackbar("Not compatible with this histogram!");
          return;
        }
        let newHistogramConfig = this.props.histogramConfig;
        let stateToSet = true;
        let newStateObject = {};
        for (let i = 0; i < loadedConfig.channels.length; i++) {
          let c = loadedConfig.channels[i];
          if (c.enabled) {
            selectedCs.push(i);
            if (stateToSet) {
              let validGamma = validateGamma(c.gamma);
              newStateObject = {
                min: c.min,
                max: c.max,
                gamma: validGamma,
                leftGammaValue: this.gammaFunction(0.25, validGamma),
                rightGammaValue: this.gammaFunction(0.75, validGamma),
              };
              stateToSet = false;
            }
          }
        }
        if (this.state.isBrightfield) selectedCs = [-1];

        newStateObject.selectedChannels = selectedCs;
        // Apply channelSelectMode if provided
        if (
          loadedConfig.channelSelectMode === "single" ||
          loadedConfig.channelSelectMode === "all"
        ) {
          newStateObject.channelSelectMode = loadedConfig.channelSelectMode;
          try {
            this.props.changeChannelSelectMode(loadedConfig.channelSelectMode);
          } catch (e2) {
            // parent may not support it; ignore
          }
          try {
            newHistogramConfig.channelSelectMode =
              loadedConfig.channelSelectMode;
          } catch (e3) {
            // ignore mutation failure
          }
        }
        // Apply fit if provided and valid
        {
          const importedFit =
            loadedConfig.fit !== undefined
              ? loadedConfig.fit
              : loadedConfig.Fit;
          const validFits = Object.values(HistogramFit);
          if (validFits.includes(importedFit)) {
            newHistogramConfig.fit = importedFit;
          }
        }
        Object.assign(newHistogramConfig, {
          scale: loadedConfig.scale
            ? loadedConfig.scale
            : HistogramScale.LOGARITHMIC,
        });

        this.setMountedState(newStateObject);
        newHistogramConfig.channels = loadedConfig.channels;

        this.props.onChange(newHistogramConfig);
        this.forceUpdateWithTimeout(10);
      } catch (e) {
        console.log("import errror:", e);
        window.showErrorSnackbar("File not supported!");
      }
    };
    fr.readAsText(files.item(0));
    // Allow selecting the same file again by clearing the input value
    try {
      e.target.value = "";
    } catch (clearErr) {
      // non-fatal if browser disallows programmatic clear
    }
  };

  initData() {
    const { histogramConfig } = this.props;
    const { histograms, selectedChannels } = this.state;

    let datasets = [];
    const channelsArray = Array.isArray(histogramConfig.channels)
      ? histogramConfig.channels
      : [];

    // RGB Brightfield: OME often exposes this as a single "RGB" channel, but the histogram data
    // is computed per RGB component. Render 3 curves (R/G/B) instead of a single white curve.
    const isRgbBrightfield =
      !!this.state.isBrightfield &&
      Array.isArray(histograms) &&
      histograms.length >= 3 &&
      channelsArray.length === 1;

    if (isRgbBrightfield) {
      datasets = [
        {
          label: "R",
          backgroundColor: "rgba(255, 0, 0, 0)",
          borderColor: "#ff0000",
          borderWidth: 2,
          data: histograms[0],
          channelIndex: -1,
        },
        {
          label: "G",
          backgroundColor: "rgba(0, 255, 0, 0)",
          borderColor: "#00ff00",
          borderWidth: 2,
          data: histograms[1],
          channelIndex: -1,
        },
        {
          label: "B",
          backgroundColor: "rgba(0, 0, 255, 0)",
          borderColor: "#0000ff",
          borderWidth: 2,
          data: histograms[2],
          channelIndex: -1,
        },
      ];

      this.data = {
        labels: Array.from(Array(256), (x, index) => index),
        datasets: datasets,
      };
      return;
    }

    let isBrightfield =
      (histograms.length === 0 || histograms.length === 3) &&
      histogramConfig.channels.length === 1;
    if (selectedChannels.includes(-1)) {
      // display all channels
      for (let c = 0; c < channelsArray.length; c++) {
        const channel = channelsArray[c];
        if (!channel) continue;
        // skip disabled channels
        if (!channel.enabled || !histograms[c]) continue;

        // push channel to dataset
        datasets.push({
          label: channel.name || `Channel ${c + 1}`,
          backgroundColor:
            channel.color !== -1
              ? hexToRGB(channel.color, 0.2)
              : hexToRGB("#000000", 0.2),
          borderColor: channel.color !== -1 ? channel.color : "#000000",
          borderWidth: 1,
          data: histograms[c],
          channelIndex: isBrightfield ? -1 : c,
        });
      }
    } else {
      for (let selectedChannel of selectedChannels) {
        if (typeof histograms[selectedChannel] === "undefined") continue;
        const channel = channelsArray[selectedChannel];
        if (!channel) continue;
        // dispaly single selected channel
        datasets.push({
          label: channel.name || `Channel ${selectedChannel + 1}`,
          backgroundColor:
            channel.color !== -1
              ? hexToRGB(channel.color, 0.2)
              : hexToRGB("#000000", 0.2),
          borderColor: channel.color !== -1 ? channel.color : "#000000",
          borderWidth: 1,
          data: histograms[selectedChannel],
          channelIndex: isBrightfield ? -1 : selectedChannel,
        });
      }
    }
    this.data = {
      labels: Array.from(Array(256), (x, index) => index),
      datasets: datasets,
    };
  }

  /**
   * Determines if the project type is related to Histo modules.
   *
   * @returns {boolean} - Returns true if the project type includes "HistoPointCounting" or "HistoClassification", otherwise false.
   */
  isHistoModule() {
    const { projectType } = this.props;
    if (!projectType) return false;
    return (
      projectType.includes("HistoPointCounting") ||
      projectType.includes("HistoClassification")
    );
  }

  render() {
    const { histogramConfig, classes } = this.props;
    const { dimensions, isBrightfield } = this.state;
    const projectType = this.props.projectType || "";

    this.initData();
    if (this.props.visible === false)
      return (
        <div className={classes.root} ref={(el) => (this.container = el)}></div>
      );

    return (
      <div className={classes.root} ref={(el) => (this.container = el)}>
        <div className={classes.toolbar}>
          <Tooltip disableInteractive title="Reset">
            <IconButton
              style={{
                float: "right",
                marginTop: this.isHistoModule() ? "-34px" : "0px",
              }}
              className={classes.toolButtonRight}
              onClick={() => this.handleReset()}
              size="large"
            >
              <FontAwesomeIcon icon={faUndo} />
            </IconButton>
          </Tooltip>
          {!this.isHistoModule() && (
            <div>
              <Tooltip disableInteractive title="Linear / logarithmic">
                <IconButton
                  className={classes.toolButton}
                  style={{
                    color:
                      histogramConfig.scale === HistogramScale.LOGARITHMIC &&
                      "#3F51B5",
                  }}
                  onClick={() => this.handleLogLin()}
                  size="large"
                >
                  <FontAwesomeIcon icon={faChartArea} />
                </IconButton>
              </Tooltip>

              {!isBrightfield && (
                <React.Fragment>
                  <Tooltip disableInteractive title="Min-max-fit">
                    <IconButton
                      className={classes.toolButton}
                      onClick={() => this.handleClickMinMaxFitButton()}
                      style={{
                        color:
                          histogramConfig.fit === HistogramFit.MIN_MAX &&
                          "#3F51B5",
                      }}
                      size="large"
                    >
                      <FontAwesomeIcon icon={faArrowsAltH} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip disableInteractive title="Best-fit">
                    <IconButton
                      className={classes.toolButton}
                      onClick={() => this.handleClickBestFitButton()}
                      style={{
                        color:
                          histogramConfig.fit === HistogramFit.BEST_FIT &&
                          "#3F51B5",
                      }}
                      size="large"
                    >
                      <FontAwesomeIcon icon={faChartLine} />
                    </IconButton>
                  </Tooltip>
                </React.Fragment>
              )}
              <Tooltip disableInteractive title="Export histogram settings">
                <IconButton
                  className={classes.toolButton}
                  onClick={this.onExportHistogramParameters}
                  size="large"
                >
                  <GetApp />
                </IconButton>
              </Tooltip>
              <Tooltip disableInteractive title="Import histogram settings">
                <IconButton
                  className={classes.toolButton}
                  onClick={() =>
                    document.getElementById("selectHistogramFile").click()
                  }
                  size="large"
                >
                  <Publish />
                </IconButton>
              </Tooltip>
              <input
                type="file"
                id="selectHistogramFile"
                accept=".json, .hishsa"
                style={{ position: "absolute", top: "-150px" }}
                onChange={this.onImportHistogramParameters}
              />
            </div>
          )}
        </div>
        <div className="histogram-outer">
          <div className={classes.chartContainer} height={null} width={null}>
            {dimensions && this.renderHistogram()}
          </div>
          <div className={classes.gradientBar} />
          <div className="slidecontainer">
            <input
              ref={(c) => (this.slider1 = c)}
              onChange={() => {
                this.setMountedState({
                  min: Math.min(
                    parseFloat(this.slider1.value),
                    this.state.max - 5,
                  ),
                });
              }}
              onMouseUp={() => {
                let min = Math.min(
                  parseFloat(this.slider1.value),
                  this.state.max - 5,
                );
                let max = this.state.max;
                this.handleMinMaxRange([min, max], this.state.gamma);
                this.slider1.blur();
              }}
              value={this.state.min}
              type="range"
              min="0"
              max="255"
              className="slider-bar"
            />
            {dimensions && (
              <input
                ref={(c) => (this.sliderGamma = c)}
                style={{
                  marginLeft: (dimensions.width / 255) * this.state.min + "px",
                  width:
                    (dimensions.width * (this.state.max - this.state.min)) /
                      255 +
                    "px",
                }}
                onChange={() => {
                  let gamma = parseFloat(this.sliderGamma.value);
                  this.updateGamma(gamma);
                  this.drawGammaLine();
                }}
                onMouseUp={() => {
                  this.handleGammaChange();
                  this.sliderGamma.blur();
                }}
                value={this.state.gamma}
                type="range"
                min="0.00"
                max="1.99"
                step="0.01"
                className="slider"
              />
            )}

            <input
              ref={(c) => (this.slider2 = c)}
              onChange={() => {
                this.setMountedState({
                  max: Math.max(
                    parseFloat(this.slider2.value),
                    this.state.min + 5,
                  ),
                });
              }}
              onMouseUp={() => {
                let min = this.state.min;
                let max = Math.max(this.slider2.value, min + 5);
                this.handleMinMaxRange([min, max], this.state.gamma);
                this.slider2.blur();
              }}
              value={this.state.max}
              type="range"
              min="0"
              max="255"
              className="slider-bar"
            />
          </div>
        </div>
        {!isBrightfield &&
          !(
            projectType.includes("HistoPointCounting") ||
            projectType.includes("HistoClassification")
          ) && (
            <React.Fragment>
              <div className={classes.controlsRow}>
                <Button
                  variant="contained"
                  color={this.areAllChannelsSelected() ? "primary" : "inherit"}
                  size="small"
                  onClick={() => this.handleAllClick()}
                >
                  ALL
                </Button>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="CheckboxSingleChannel"
                      onChange={(e, value) =>
                        this.changeChannelSelectMode(e, value)
                      }
                      color="primary"
                      checked={this.state.channelSelectMode === "single"}
                      value="singleChannelMode"
                    />
                  }
                  label="Single Channel"
                />
              </div>
              <div style={{ maxHeight: "200px", overflow: "auto" }}>
                <ToggleButtonGroup
                  className={classes.toggleContainer}
                  value={this.state.selectedChannels}
                  onChange={(e, value) => this.changeChannel(value)}
                >
                  {histogramConfig.channels.map((channel, index) => (
                    <Tooltip
                      key={index}
                      disableInteractive
                      title={"Toggle with [ALT] + [" + (index + 1) + "]"}
                    >
                      <ToggleButton
                        id={"channel_" + index}
                        value={index}
                        size="small"
                        className={classes.toggleButtonTight}
                      >
                        {(() => {
                          const isActive =
                            this.state.selectedChannels.includes(index);
                          const containerClass = isActive
                            ? classes.toggleBtnContentActive
                            : classes.toggleBtnContent;
                          const labelClass = isActive
                            ? classes.channelLabelActive
                            : classes.channelLabelInactive;
                          return (
                            <span
                              className={
                                containerClass + " " + classes.channelItem
                              }
                              style={{
                                backgroundColor: isActive
                                  ? channel.color && channel.color !== -1
                                    ? hexToRGB(channel.color, 0.12)
                                    : "rgba(0,0,0,0.06)"
                                  : "transparent",
                              }}
                            >
                              <div className={classes.channelSwatchWrapper}>
                                <SketchColorPicker
                                  color={channel.color}
                                  openOn="doubleClick"
                                  onSwatchSingleClick={() =>
                                    this.toggleChannelFromSwatch(index)
                                  }
                                  handleChange={(color) => {
                                    this.handleColorPickerChange(color, index);
                                  }}
                                />
                              </div>
                              <div
                                className={
                                  classes.channelLabel + " " + labelClass
                                }
                              >
                                {channel.name}
                              </div>
                            </span>
                          );
                        })()}
                      </ToggleButton>
                    </Tooltip>
                  ))}
                </ToggleButtonGroup>
              </div>
            </React.Fragment>
          )}
      </div>
    );
  }
}

// define the component's interface
Histogram.propTypes = {
  classes: PropTypes.object.isRequired,
  histogramConfig: PropTypes.object,
  bitDepth: PropTypes.number,
  channelSelectMode: PropTypes.string.isRequired,
  changeChannelSelectMode: PropTypes.func,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  projectType: PropTypes.string,
  projectContext: PropTypes.object,
  visible: PropTypes.bool,
  fileName: PropTypes.string,
  // Optional callback to trigger a zoom-to-fit in the parent when reset is clicked
  onResetZoomFit: PropTypes.func,
};

export default withStyles(styles)(Histogram);
